import React, { useRef, useEffect, memo, useMemo } from "react";
import { useLtpStore } from "../contexts/LTPStore/useLTPStore";
import { calculatePositionPnL } from "../utils/calculatePositionPnL";

/**
 * Optimized cell component that updates using refs instead of state
 * This prevents unnecessary re-renders during high-frequency updates
 */
export const OptimizedNumberCell = memo(
  ({ value, className = "", formatFn = null }) => {
    const cellRef = useRef(null);
    const prevValueRef = useRef(value);

    useEffect(() => {
      if (cellRef.current && prevValueRef.current !== value) {
        const displayValue = formatFn ? formatFn(value) : value ?? "-";
        cellRef.current.textContent = displayValue;
        prevValueRef.current = value;

        // Add flash animation on change
        cellRef.current.classList.add("cell-flash");
        setTimeout(() => {
          cellRef.current?.classList.remove("cell-flash");
        }, 300);
      }
    }, [value, formatFn]);

    const initialDisplayValue = formatFn ? formatFn(value) : value ?? "-";

    return (
      <span ref={cellRef} className={className}>
        {initialDisplayValue}
      </span>
    );
  }
);

OptimizedNumberCell.displayName = "OptimizedNumberCell";

/**
 * Optimized cell for colored validation cells (QTY, SL QTY)
 */
export const OptimizedColorCell = memo(
  ({
    value,
    color,
    expectedValue = null,
    formatFn = null,
    className = "",
    applyColorToCell = false,
  }) => {
    const cellRef = useRef(null);
    const prevValueRef = useRef({ value, color });

    const getColorClasses = (colorValue) => {
      switch (colorValue) {
        case "green":
          return "bg-green-500/20 text-green-400";
        case "red":
          return "bg-red-500/20 text-red-400";
        case "orange":
          return "bg-orange-600/50 text-orange-100 font-bold";
        default:
          return "";
      }
    };

    // Apply color to parent td on mount
    useEffect(() => {
      if (cellRef.current && applyColorToCell) {
        const tdElement = cellRef.current.parentElement;
        if (tdElement) {
          const colorClasses = getColorClasses(color);
          if (colorClasses) {
            const classes = colorClasses.split(" ");
            tdElement.classList.add(...classes);
          }
        }
      }
    }, [applyColorToCell, color]);

    useEffect(() => {
      if (
        cellRef.current &&
        (prevValueRef.current.value !== value ||
          prevValueRef.current.color !== color)
      ) {
        const displayValue = formatFn ? formatFn(value) : value ?? "-";
        cellRef.current.textContent = displayValue;

        // Update color classes on parent td or on span
        if (applyColorToCell) {
          const tdElement = cellRef.current.parentElement;
          if (tdElement) {
            // Remove previous color classes from td
            tdElement.classList.remove(
              "bg-green-500/20",
              "text-green-400",
              "bg-red-500/20",
              "text-red-400",
              "bg-orange-600/50",
              "text-orange-100",
              "font-bold"
            );
            // Add new color classes to td
            const colorClasses = getColorClasses(color);
            if (colorClasses) {
              const classes = colorClasses.split(" ");
              tdElement.classList.add(...classes);
            }
          }
        } else {
          // Apply color classes to span
          cellRef.current.className = className + " " + getColorClasses(color);
        }

        prevValueRef.current = { value, color };

        // Add flash animation on change
        cellRef.current.classList.add("cell-flash");
        setTimeout(() => {
          cellRef.current?.classList.remove("cell-flash");
        }, 300);
      }
    }, [value, color, formatFn, className, applyColorToCell]);

    const initialDisplayValue = formatFn ? formatFn(value) : value ?? "-";

    return (
      <span
        ref={cellRef}
        className={className}
        title={
          expectedValue !== null
            ? `Expected: ${formatFn ? formatFn(expectedValue) : expectedValue}`
            : undefined
        }
      >
        {initialDisplayValue}
      </span>
    );
  }
);

OptimizedColorCell.displayName = "OptimizedColorCell";

/**
 * Optimized PNL cell with color coding
 */
export const OptimizedPNLCell = memo(({ value, className = "" }) => {
  const cellRef = useRef(null);
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (cellRef.current && prevValueRef.current !== value) {
      if (value === undefined || value === null || value === 0) {
        cellRef.current.textContent = "-";
        cellRef.current.className = `${className} text-gray-500`;
      } else {
        const isProfit = value >= 0;
        const sign = isProfit ? "+" : "";
        const formattedValue = (value ?? 0).toLocaleString("en-IN");
        cellRef.current.textContent = `${sign}${formattedValue}`;
        cellRef.current.className = `${className} ${isProfit ? "text-green-400" : "text-red-400"
          } font-semibold`;
      }

      prevValueRef.current = value;

      // Add flash animation on change
      cellRef.current.classList.add("cell-flash");
      setTimeout(() => {
        cellRef.current?.classList.remove("cell-flash");
      }, 300);
    }
  }, [value, className]);

  // Initial render
  const getInitialContent = () => {
    if (value === undefined || value === null || value === 0) {
      return { text: "-", colorClass: "text-gray-500" };
    }
    const isProfit = value >= 0;
    const sign = isProfit ? "+" : "";
    const formattedValue = (value ?? 0).toLocaleString("en-IN");
    return {
      text: `${sign}${formattedValue}`,
      colorClass: isProfit ? "text-green-400" : "text-red-400",
    };
  };

  const initial = getInitialContent();

  return (
    <span
      ref={cellRef}
      className={`${className} ${initial.colorClass} font-semibold`}
    >
      {initial.text}
    </span>
  );
});


OptimizedPNLCell.displayName = "OptimizedPNLCell";

/**
 * Live LTP Cell that subscribes directly to useLtpStore
 * Prevents parent re-renders when price updates
 */
export const LiveLTPCell = memo(({ symbol, className = "", formatFn = null }) => {
  const ltp = useLtpStore((state) => state.ltps[symbol]);
  const cellRef = useRef(null);
  const prevValueRef = useRef(ltp);

  useEffect(() => {
    if (cellRef.current && prevValueRef.current !== ltp) {
      const displayValue = formatFn ? formatFn(ltp) : ltp ?? "-";
      cellRef.current.textContent = displayValue;
      prevValueRef.current = ltp;

      // Add subtle flash for price changes
      cellRef.current.classList.add("price-flash");
      setTimeout(() => {
        cellRef.current?.classList.remove("price-flash");
      }, 200);
    }
  }, [ltp, formatFn]);

  const initialDisplayValue = formatFn ? formatFn(ltp) : ltp ?? "-";

  return (
    <span ref={cellRef} className={className}>
      {initialDisplayValue}
    </span>
  );
});

LiveLTPCell.displayName = "LiveLTPCell";

/**
 * Live PNL Cell for a single position
 * Subscribes to LTP updates for the specific symbol
 */
export const LivePNLCell = memo(({ position, className = "" }) => {
  const symbol = position?.symbol;
  const ltp = useLtpStore((state) => state.ltps[symbol]);
  const cellRef = useRef(null);
  const prevValueRef = useRef(null);

  useEffect(() => {
    if (cellRef.current && ltp !== undefined && prevValueRef.current !== ltp) {
      const pnl = calculatePositionPnL(position, ltp);
      
      if (pnl === undefined || pnl === null || pnl === 0) {
        cellRef.current.textContent = "-";
        cellRef.current.className = `${className} text-gray-500`;
      } else {
        const isProfit = pnl >= 0;
        const sign = isProfit ? "+" : "";
        const formattedValue = Math.abs(pnl).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        cellRef.current.textContent = `${sign}${formattedValue}`;
        cellRef.current.className = `${className} ${isProfit ? "text-green-400" : "text-red-400"} font-semibold`;
      }
      
      prevValueRef.current = ltp;
    }
  }, [ltp, position, className]);

  // Initial render logic
  const initialContent = useMemo(() => {
    const currentLtp = ltp !== undefined ? ltp : (position?.ltp || position?.current_ltp);
    const pnl = currentLtp ? calculatePositionPnL(position, currentLtp) : (position?.pl || position?.pnl || 0);

    if (pnl === 0) return { text: "-", color: "text-gray-500" };
    const isProfit = pnl >= 0;
    const sign = isProfit ? "+" : "";
    const formattedValue = Math.abs(pnl).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return {
      text: `${sign}${formattedValue}`,
      color: isProfit ? "text-green-400" : "text-red-400"
    };
  }, [position, ltp]);

  return (
    <span ref={cellRef} className={`${className} ${initialContent.color} font-semibold`}>
      {initialContent.text}
    </span>
  );
});

LivePNLCell.displayName = "LivePNLCell";

/**
 * Live Aggregated PNL Cell for a group of positions
 * Subscribes only to relevant symbol updates
 */
export const LiveAggregatedPNLCell = memo(({ positions = [], className = "" }) => {
  // Collect all unique symbols in this group
  const symbols = useMemo(() => {
    const s = new Set();
    positions.forEach(p => p.symbol && s.add(p.symbol));
    return Array.from(s);
  }, [positions]);

  // Subscribe to all relevant LTPs
  const totalPnl = useLtpStore((state) => {
    let sum = 0;
    positions.forEach(p => {
      const ltp = state.ltps[p.symbol];
      if (ltp) {
        sum += calculatePositionPnL(p, ltp);
      } else {
        // Fallback to existing P&L if LTP not in store yet
        sum += (p.pl || p.pnl || 0);
      }
    });
    return sum;
  });

  const cellRef = useRef(null);
  const prevValueRef = useRef(totalPnl);

  useEffect(() => {
    if (cellRef.current && prevValueRef.current !== totalPnl) {
      if (totalPnl === 0) {
        cellRef.current.textContent = "-";
        cellRef.current.className = `${className} text-gray-500`;
      } else {
        const isProfit = totalPnl >= 0;
        const sign = isProfit ? "+" : "";
        const formattedValue = Math.abs(totalPnl).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        cellRef.current.textContent = `${sign}${formattedValue}`;
        cellRef.current.className = `${className} ${isProfit ? "text-green-400" : "text-red-400"} font-semibold text-right`;
      }
      prevValueRef.current = totalPnl;
    }
  }, [totalPnl, className]);

  // Initial render content
  const initialContent = useMemo(() => {
    if (totalPnl === 0) return { text: "-", color: "text-gray-500" };
    const isProfit = totalPnl >= 0;
    const sign = isProfit ? "+" : "";
    const formattedValue = Math.abs(totalPnl).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return {
      text: `${sign}${formattedValue}`,
      color: isProfit ? "text-green-400" : "text-red-400"
    };
  }, [totalPnl]);

  return (
    <span ref={cellRef} className={`${className} ${initialContent.color} font-semibold text-right transition-colors duration-200`}>
      {initialContent.text}
    </span>
  );
});

LiveAggregatedPNLCell.displayName = "LiveAggregatedPNLCell";
